import { t } from '@/i18n/t';
import { discountMagnitude, discountSpan, type BillingCadence } from '@/components/OrderSummary';
import type { OnsitePrice, PromoDiscount, PromoValidationResponse, TrialDiscount, ChargedTotals } from '@/types';

// promoDiscountFromValidation lifts the discount fields off a validated code into
// the shape the order summary and chip consume. Duration defaults to once so an
// older backend that omits it previews conservatively (first charge only).
export function promoDiscountFromValidation(v: PromoValidationResponse): TrialDiscount {
  return {
    percentOff: v.percentOff,
    amountOff: v.amountOff,
    currency: v.currency,
    duration: v.duration ?? 'once',
    durationInMonths: v.durationInMonths,
  };
}

// firstChargeFromValidation lifts Stripe's invoice-preview totals off a
// validated code — what an immediate convert would charge. Null when the
// backend returned no preview (invalid code, or the preview call failed).
export function firstChargeFromValidation(v: PromoValidationResponse): ChargedTotals | null {
  if (v.firstChargeAmountCents == null || !v.firstChargeCurrency) {
    return null;
  }
  return { amountCents: v.firstChargeAmountCents, currency: v.firstChargeCurrency, discountCents: v.firstChargeDiscountCents };
}

// PromoScope is how an applied discount recurs relative to the purchase, driving
// the chip copy. one_time = a single-charge purchase; first_payment = a
// once-duration coupon on a subscription (only its first invoice); forever =
// every invoice; limited = a repeating coupon whose month count is unknown
// (fallback only — every path that has the count asserts it); {months} = the
// next N invoices (repeating coupon, the count exact).
export type PromoScope = 'one_time' | 'first_payment' | 'forever' | 'limited' | { months: number };

// promoChipLabel builds the applied-promo chip's summary from a pre-formatted
// magnitude ("25%" or "$5.00") and its scope, e.g. "25% off forever",
// "$5.00 off", "25% off for 6 months". Both checkout paths share this so the same
// coupon reads identically whether it's applied via the Stripe Checkout Session
// (paid) or resolved by the validate endpoint (trial).
export function promoChipLabel(magnitude: string, scope: PromoScope): string {
  if (scope === 'one_time') {
    return t('promo.chip.off', { magnitude });
  }
  if (scope === 'first_payment') {
    return t('promo.chip.offFirstPayment', { magnitude });
  }
  if (scope === 'forever') {
    return t('promo.chip.offForever', { magnitude });
  }
  if (scope === 'limited') {
    return t('promo.chip.offLimited', { magnitude });
  }
  return t('promo.chip.offMonths', { n: scope.months, count: scope.months, magnitude });
}

// couponChipLabel builds the applied chip label for a validated coupon on a
// recurring price (trial form, trial-denial convert, their success screens).
// Shares the order summary's magnitude and charge span so the chip and the
// summary never disagree. Undefined when there's no usable discount (e.g. a
// fixed amount in a currency Stripe wouldn't apply to this price).
export function couponChipLabel(price: OnsitePrice, discount: TrialDiscount): string | undefined {
  const magnitude = discountMagnitude(price, discount);
  if (magnitude == null) {
    return undefined;
  }
  return promoChipLabel(magnitude, couponScope(price, discount));
}

// couponScope maps a coupon onto a recurring cadence's charges for the chip:
// a window covering only the first charge reads "first payment"; a window the
// cadence can't express keeps the stated month count, or "limited" without one.
export function couponScope(cadence: BillingCadence, discount: Pick<TrialDiscount, 'duration' | 'durationInMonths'>): PromoScope {
  const span = discountSpan(cadence, discount);
  if (span === 'first') {
    return 'first_payment';
  }
  if (span === 'every') {
    return 'forever';
  }
  if (typeof span === 'object') {
    return span;
  }
  return discount.durationInMonths ? { months: discount.durationInMonths } : 'limited';
}

// trialDiscountFromPromo maps the config block's per-price discount onto the
// order summary's discount shape (the gate's discount note).
export function trialDiscountFromPromo(discount: PromoDiscount): TrialDiscount {
  return {
    percentOff: discount.percentOff,
    amountOff: discount.amountOff,
    currency: discount.currency,
    duration: (discount.duration as TrialDiscount['duration']) ?? 'once',
    durationInMonths: discount.durationInMonths,
  };
}

// pickerPromoLabel builds the code-applied line's deal label ("$4.00 off
// first payment", "20% off for 3 months") with the same scope the payment
// step's chip uses; a one-time price has no duration ("$4.00 off"). Undefined
// when the discount isn't usable on the price (e.g. a fixed amount in a
// currency Stripe wouldn't apply to it).
export function pickerPromoLabel(price: OnsitePrice, discount: PromoDiscount): string | undefined {
  const resolved = trialDiscountFromPromo(discount);
  if (price.type === 'recurring') {
    return couponChipLabel(price, resolved);
  }
  const magnitude = discountMagnitude(price, resolved);
  return magnitude == null ? undefined : promoChipLabel(magnitude, 'one_time');
}

// PROMO_REASON_KEYS maps the validate endpoint's machine reasons to copy keys.
// Shared by the trial path (code checked pre-submit) and the paid path (code
// checked before applyPromotionCode, since Stripe needs the namespaced
// Stripe-side string from the same response). Every key interpolates {code} —
// a campaign auto-apply failure names the code the visitor never typed.
const PROMO_REASON_KEYS: Record<string, string> = {
  not_found: 'promo.invalid',
  inactive: 'promo.reason.inactive',
  expired: 'promo.reason.expired',
  product_not_eligible: 'promo.reason.productNotEligible',
  minimum_not_met: 'promo.reason.minimumNotMet',
  redemption_limit: 'promo.reason.redemptionLimit',
  currency_not_eligible: 'promo.reason.currencyNotEligible',
};

export function promoReasonMessage(code: string, reason?: string): string {
  return t((reason && PROMO_REASON_KEYS[reason]) || 'promo.invalid', { code });
}

// displayPromoCode maps a session discount's code string back to the
// publisher-facing code a visitor recognizes: codes created after namespacing
// carry the `{domainId}-` prefix at Stripe (Stripe matches code strings
// account-wide, so the prefix is what keeps one publisher's code off another
// publisher's checkout). Legacy codes have no prefix and pass through.
export function displayPromoCode(domainId: number | undefined, code: string): string {
  const prefix = domainId === undefined ? '' : `${domainId}-`;
  return prefix && code.startsWith(prefix) ? code.slice(prefix.length) : code;
}
